import { motion } from "framer-motion";
import AboutUs from "./about";
import CustomerReviews from "./review";
import PricingSection from "./pricing";
import BenefitsSlider from "./benefit";
import ProofOfWorkSection from "./proof";
import ContactUs from "./form";
import FAQSection from "./faq";
import MediaGallery from "./img";
import constantObj from "../constant/constant";
import { useSocial } from "../hooks/useSocial";
import { FaRobot, FaBolt, FaRocket, FaCheckCircle, FaTelegramPlane } from "react-icons/fa";

const robots = [
  {
    heading: "MetaTrader 4 Robot",
    list: ["Easy To Install — Plug & Play setup", "Robot works 24 hours, 5 days a week"],
    btnTxt: "Pricing Overview",
    gridspan: "col-span-2",
    icon: <FaRobot className="text-2xl" />,
    color: "#22c55e",
    imgSrc: "../asset/img.png",
  },
  {
    heading: "MetaTrader 5 Robot",
    list: ["99% winning rate", "Unlimited License Available", "Works on any broker of your choice"],
    btnTxt: "Pricing Overview",
    icon: <FaBolt className="text-2xl" />,
    color: "#3b82f6",
  },
  {
    heading: "High Frequency Trading Bot",
    list: [
      "Works on TradeLocker, MatchTrader, DXTrade",
      "Passes evaluation stage faster",
      "Equity protection with accurate risk management",
    ],
    btnTxt: "Raw Pricing Benefits",
    icon: <FaRocket className="text-2xl" />,
    color: "#f59e0b",
  },
];

export default function MainBody() {
  const { socials } = useSocial();
  const telegram = socials?.find((item) => item.label?.toLowerCase() === "telegram");
  
const admin = socials.find((i) => i.label?.toLowerCase() === "admin");

  return (
    <main style={{ background: "var(--uea-black)" }}>
      <AboutUs />
      <BenefitsSlider />
      <MediaGallery />
      <CustomerReviews />
      <ProofOfWorkSection />

      {/* Available Robots Section */}
      <section className="py-24 px-6" style={{ background: "var(--uea-dark)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-mono-code text-green-400 tracking-widest uppercase">// Robot Suite</span>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="font-orbitron font-black text-3xl sm:text-4xl text-white mt-4">
              Available{" "}
              <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Trading Robots
              </span>
            </motion.h2>
            <p className="text-slate-400 mt-4 max-w-xl mx-auto text-sm">
              Choose the robot that fits your trading platform. All robots come with full support and optimized set files.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {robots.map((robot, i) => (
              <RobotCard key={i} robot={robot} telegram={telegram} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* <PricingSection /> */}
      <FAQSection />
      <ContactUs />
    </main>
  );
}

function RobotCard({ robot, telegram, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: index * 0.1 }} whileHover={{ y: -6, transition: { duration: 0.2 } }}
      className={`relative rounded-2xl p-0.5 overflow-hidden ${robot.gridspan ? "lg:col-span-2" : ""}`}
      style={{ background: `linear-gradient(135deg, ${robot.color}30, rgba(59,130,246,0.15))` }}>
      <motion.div className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
        style={{ background: `radial-gradient(circle at top right, ${robot.color}20, transparent)` }}
        animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 3, repeat: Infinity }} />

      <div className="rounded-2xl p-8 h-full relative" style={{ background: "var(--uea-card)" }}>
        <div className={`flex ${robot.imgSrc ? "flex-col lg:flex-row" : "flex-col"} gap-8 items-start`}>
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${robot.color}15`, color: robot.color, border: `1px solid ${robot.color}30` }}>
                {robot.icon}
              </div>
              <h3 className="font-orbitron text-white font-bold text-xl leading-tight">{robot.heading}</h3>
            </div>

            <ul className="space-y-3 mb-8">
              {robot.list.map((item, j) => (
                <motion.li key={j} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }} transition={{ delay: j * 0.1 }}
                  className="flex items-start gap-3 text-sm text-slate-300">
                  <FaCheckCircle className="flex-shrink-0 mt-0.5" style={{ color: robot.color }} />
                  {item}
                </motion.li>
              ))}
            </ul>

            <motion.a href={telegram?.url || "#"}
              whileHover={{ scale: 1.04, boxShadow: `0 0 25px ${robot.color}40` }} whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 font-orbitron text-xs tracking-widest uppercase font-bold px-8 py-3.5 rounded-xl transition-all"
              style={{ background: `linear-gradient(135deg, ${robot.color}, ${robot.color}bb)`, color: robot.color === "#f59e0b" ? "#000" : "#fff" }}>
              <FaTelegramPlane /> {robot.btnTxt}
            </motion.a>
          </div>

          {robot.imgSrc && (
            <div className="flex-shrink-0 w-full lg:w-1/2">
              <motion.img src={robot.imgSrc} alt={robot.heading} className="w-full rounded-xl shadow-2xl"
                whileHover={{ scale: 1.02 }} style={{ boxShadow: `0 0 40px ${robot.color}20` }} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
